<template>
  <!-- 인증서 목록에서 화면 이탈 없이 팔로워 정보를 조회하는 Drawer.
       customerMobile 하나로는 unionId 를 바로 알 수 없어 2단계로 조회한다:
       1) /cn/wechat/followers?search=<mobile> 로 후보 목록 조회 (백엔드가 nickname|name|mobile OR 매칭이라 무관한 사람이 섞일 수 있음)
       2) 후보가 정확히 1명이고 unionId 가 있을 때만 자동으로 /cn/wechat/followers/detail 조회.
       2명 이상이면 절대 자동 선택하지 않고 운영자가 직접 고르게 한다 — 다른 환자 정보를 잘못 보여주는 것을
       막기 위한 이 기능의 존재 이유 그 자체다. -->
  <a-drawer
    :visible="visible"
    :width="drawerWidth"
    placement="right"
    :body-style="{ padding: '16px', overflowY: 'auto' }"
    destroy-on-close
    @close="onClose"
  >
    <template #title>
      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
        <span>{{ $t('follower_lookup_drawer_title') }}</span>
        <span v-if="customerName" class="follower-lookup-name-badge">{{ customerName }}</span>
      </div>
    </template>

    <a-spin :spinning="loading" :tip="$t('follower_lookup_loading')">
      <!-- 에러 상태 — LotTraceDetail.vue 와 동일한 a-alert + 재시도 버튼 패턴 재사용 -->
      <a-alert
        v-if="phase === 'error'"
        type="error"
        :message="errorMessage"
        show-icon
        style="margin-bottom: 16px;"
      >
        <template #action>
          <a-button size="small" @click="onRetry">{{ $t('lotTrace_actions_retry') }}</a-button>
        </template>
      </a-alert>

      <!-- 0건 -->
      <div v-else-if="phase === 'empty'" class="follower-lookup-empty">
        <i class="fa-solid fa-exclamation-triangle" />
        <p class="follower-lookup-empty-title">{{ $t('follower_lookup_empty') }}</p>
        <p class="follower-lookup-empty-hint">{{ $t('follower_lookup_empty_hint') }}</p>
        <p class="follower-lookup-query-condition">
          {{ $t('follower_lookup_query_condition', { value: mobile }) }}
        </p>
      </div>

      <!-- 2명 이상(또는 1명이지만 unionId 미보유) — 절대 자동 선택하지 않고 운영자가 직접 고른다 -->
      <div v-else-if="phase === 'multiple'" class="follower-lookup-multiple">
        <a-alert
          type="warning"
          show-icon
          :message="$t('follower_lookup_multiple_warning')"
          style="margin-bottom: 12px;"
        />
        <a-radio-group v-model:value="selectedUnionId" class="follower-lookup-candidate-group">
          <div
            v-for="(candidate, idx) in candidates"
            :key="candidate.unionId || idx"
            class="follower-lookup-candidate"
          >
            <a-radio :value="candidate.unionId" :disabled="!candidate.unionId">
              <div class="follower-lookup-candidate-body">
                <div class="follower-lookup-candidate-line">
                  <span class="follower-lookup-candidate-name">
                    {{ candidate.name || candidate.nickname || '-' }}
                  </span>
                  <span class="follower-lookup-candidate-mobile">
                    {{ formatPhoneNumber(candidate.mobile) }}
                  </span>
                </div>
                <div class="follower-lookup-candidate-line follower-lookup-candidate-meta">
                  <span>{{ formatUnixTimestamp(candidate.subscribe_time) }}</span>
                  <span>{{ candidate.subscribe_scene || $t('followerlist_unknown_source') }}</span>
                </div>
                <div v-if="!candidate.unionId" class="follower-lookup-candidate-warning">
                  {{ $t('follower_lookup_unionid_missing') }}
                </div>
              </div>
            </a-radio>
          </div>
        </a-radio-group>
        <a-button
          type="primary"
          :disabled="!selectedUnionId"
          style="margin-top: 12px;"
          @click="onSelectAndView"
        >
          {{ $t('follower_lookup_select_and_view') }}
        </a-button>
      </div>

      <!-- 상세 — 목록 화면과 동일한 FollowerDetailPanel 재사용 -->
      <!-- compact: 드로어 폭(420px)에 맞춰 1열로 접는다. 목록 모달(800px)은 기본값 그대로 2열 유지 -->
      <FollowerDetailPanel v-else-if="phase === 'detail'" :data="detailData" compact />
    </a-spin>
  </a-drawer>
</template>

<script>
import followerApis from '@/views/Follower/api';
import chinaInapi from '@/apis/chinaInapi';
import FollowerDetailPanel from '@/views/Follower/components/FollowerDetailPanel.vue';
import dayjs from 'dayjs';

export default {
  name: 'FollowerLookupDrawer',
  components: { FollowerDetailPanel },
  props: {
    visible: { type: Boolean, default: false },
    // 인증서 레코드에는 unionId/openId 가 없어 customerMobile 로만 조회를 시작한다
    mobile: { type: String, default: '' },
    customerName: { type: String, default: '' }
  },
  emits: ['update:visible', 'close'],
  data() {
    return {
      apis: followerApis(chinaInapi),
      loading: false,
      // idle | empty | multiple | detail | error
      phase: 'idle',
      errorMessage: '',
      retryAction: null,
      candidates: [],
      selectedUnionId: null,
      detailData: null
    };
  },
  computed: {
    // 모바일 full-screen (≤ 767px), 데스크탑은 420px 고정
    drawerWidth() {
      return window.innerWidth <= 767 ? '100%' : 420;
    }
  },
  watch: {
    // 열릴 때마다 새로 조회한다 — 캐시하지 않음(다른 환자로 다시 열렸을 때 이전 결과 노출 방지)
    visible(newVal) {
      if (newVal) {
        this.resetAndFetch();
      }
    }
  },
  methods: {
    resetAndFetch() {
      this.phase = 'idle';
      this.errorMessage = '';
      this.retryAction = null;
      this.candidates = [];
      this.selectedUnionId = null;
      this.detailData = null;
      if (this.mobile) {
        this.fetchCandidates();
      }
    },
    // 1단계: 전화번호로 팔로워 후보 조회
    async fetchCandidates() {
      this.loading = true;
      try {
        const response = await this.apis.getFollowerList({ search: this.mobile, page: 1, limit: 20 });
        if (!response.data.success) {
          throw new Error(response.data.message || 'request failed');
        }
        const items = response.data.data.items || [];
        if (items.length === 0) {
          this.phase = 'empty';
        } else if (items.length === 1 && items[0].unionId) {
          // 후보가 정확히 1명이고 unionId 를 가지고 있을 때만 자동 조회 — 추가 클릭 없이 상세로 진입
          await this.fetchDetail(items[0].unionId);
          return;
        } else {
          // 2명 이상, 또는 유일한 후보가 unionId 없음 — 두 경우 모두 자동 선택 금지
          this.candidates = items;
          this.phase = 'multiple';
        }
      } catch (error) {
        console.error('Error fetching follower candidates:', error);
        this.errorMessage = error?.response?.data?.message || error?.message || String(error);
        this.retryAction = () => this.fetchCandidates();
        this.phase = 'error';
      } finally {
        this.loading = false;
      }
    },
    // 2단계: unionId 로 상세 조회 — 정확히 1명일 때 자동으로, 2명 이상일 때는 운영자 선택 후에만 호출된다
    async fetchDetail(unionId) {
      this.loading = true;
      try {
        const response = await this.apis.getFollowerDetail(unionId);
        if (!response.data.success) {
          throw new Error(response.data.message || 'request failed');
        }
        this.detailData = response.data.data;
        this.phase = 'detail';
      } catch (error) {
        console.error('Error fetching follower detail:', error);
        this.errorMessage = error?.response?.data?.message || error?.message || String(error);
        this.retryAction = () => this.fetchDetail(unionId);
        this.phase = 'error';
      } finally {
        this.loading = false;
      }
    },
    onSelectAndView() {
      if (!this.selectedUnionId) return;
      this.fetchDetail(this.selectedUnionId);
    },
    onRetry() {
      if (this.retryAction) {
        this.retryAction();
      }
    },
    onClose() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    // 전화번호 포맷팅 — FollowerList.vue 와 동일 규칙(중국 휴대폰 11자리 3-4-4)
    formatPhoneNumber(phone) {
      if (!phone) return '-';
      const digits = String(phone).replace(/\D/g, '');
      if (digits.length === 11) {
        return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
      }
      return phone || '-';
    },
    // Unix timestamp를 날짜 형식으로 변환 — FollowerList.vue 와 동일
    formatUnixTimestamp(timestamp) {
      if (!timestamp) return '-';
      return dayjs.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    }
  }
};
</script>

<style scoped>
.follower-lookup-name-badge {
  font-size: 12px;
  background: #f0f2f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 1px 6px;
}

.follower-lookup-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: #999;
  text-align: center;
}

.follower-lookup-empty i {
  font-size: 24px;
  margin-bottom: 8px;
}

.follower-lookup-empty-title {
  margin: 4px 0;
  color: #666;
  font-weight: 600;
}

.follower-lookup-empty-hint {
  margin: 0 0 8px;
  font-size: 13px;
  color: #999;
}

.follower-lookup-query-condition {
  margin: 0;
  font-size: 12px;
  color: #bbb;
}

.follower-lookup-candidate-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.follower-lookup-candidate {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 10px 12px;
}

.follower-lookup-candidate :deep(.ant-radio-wrapper) {
  align-items: flex-start;
  white-space: normal;
  width: 100%;
}

.follower-lookup-candidate-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.follower-lookup-candidate-line {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.follower-lookup-candidate-name {
  font-weight: 600;
}

.follower-lookup-candidate-meta {
  font-size: 12px;
  color: #999;
}

.follower-lookup-candidate-warning {
  font-size: 12px;
  color: #d4380d;
  margin-top: 2px;
}
</style>
